import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CalendarDays, MapPin, Minus, Plus, Loader2 } from "lucide-react";
import { api } from "./api";
import { useAuth } from "./AuthContext";

const fmtDate = (d) => { try { return new Date(d).toLocaleString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

export function EventDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => {
      setEv(r.data);
      if (r.data.ticket_types?.length) setSel(r.data.ticket_types[0].name);
    }).catch(() => setErr("Event not found"));
  }, [id]);

  const buy = async () => {
    if (!user) { nav("/login", { state: { from: `/events/${id}` } }); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/checkout", {
        event_id: id, ticket_name: sel, quantity: qty, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Checkout failed"); setBusy(false);
    }
  };

  if (err && !ev) return <p className="text-zinc-500">{err}</p>;
  if (!ev) return <p className="text-zinc-500">Loading…</p>;
  const selTier = ev.ticket_types?.find((t) => t.name === sel);
  const price = selTier?.price || 0;
  const remaining = selTier?.remaining ?? 0;
  const soldOut = selTier?.sold_out || remaining <= 0;
  const maxQty = Math.min(20, remaining || 1);

  return (
    <div data-testid="event-detail">
      {ev.image && (
        <div className="relative -mx-4 -mt-6 mb-6 h-56 overflow-hidden">
          <img src={ev.image} alt={ev.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 to-transparent" />
        </div>
      )}
      <h1 className="font-oswald text-4xl font-700 uppercase leading-[0.9] tracking-tighter">{ev.title}</h1>
      <div className="mt-3 flex flex-col gap-1.5 text-sm text-zinc-400">
        <span className="flex items-center gap-2"><CalendarDays size={15} className="text-gold" /> {fmtDate(ev.date)}</span>
        {ev.location && <span className="flex items-center gap-2"><MapPin size={15} className="text-gold" /> {ev.location}</span>}
      </div>
      {ev.description && <p className="mt-5 leading-relaxed text-zinc-300">{ev.description}</p>}

      <div className="mt-8 border-t border-white/10 pt-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">/ Tickets</p>
        <div className="mt-4 grid gap-3">
          {ev.ticket_types?.map((t) => (
            <button key={t.name} data-testid={`ticket-type-${t.name}`} disabled={t.sold_out}
              onClick={() => !t.sold_out && (setSel(t.name), setQty(1))}
              className={`flex items-center justify-between border px-5 py-4 text-left transition-colors ${t.sold_out ? "cursor-not-allowed border-white/5 opacity-40" : sel === t.name ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}>
              <span className="flex flex-col">
                <span className="font-oswald text-xl font-600 uppercase tracking-tight">{t.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {t.sold_out ? "Sold Out" : t.remaining <= 10 ? `Only ${t.remaining} left` : "Available"}
                </span>
              </span>
              <span className={`font-mono ${t.sold_out ? "text-zinc-600 line-through" : "text-gold"}`}>${t.price}</span>
            </button>
          ))}
          {(!ev.ticket_types || ev.ticket_types.length === 0) && <p className="text-zinc-500">No tickets available.</p>}
        </div>

        {ev.ticket_types?.length > 0 && (
          <>
            <div className="mt-6 flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">Quantity</span>
              <div className="flex items-center gap-4">
                <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-9 w-9 items-center justify-center border border-white/20 hover:border-gold"><Minus size={16} /></button>
                <span className="w-6 text-center font-oswald text-xl" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="flex h-9 w-9 items-center justify-center border border-white/20 hover:border-gold"><Plus size={16} /></button>
              </div>
            </div>
            {err && <p className="mt-4 text-sm text-red-400" data-testid="checkout-error">{err}</p>}
            <button data-testid="buy-btn" onClick={buy} disabled={busy || soldOut}
              className="mt-6 flex w-full items-center justify-center gap-2 bg-gold py-4 font-mono text-[12px] uppercase tracking-[0.25em] text-ink-950 transition-opacity hover:opacity-90 disabled:opacity-40">
              {busy ? <Loader2 size={16} className="animate-spin" /> : soldOut ? "Sold Out" : `Pay $${(price * qty).toFixed(2)}`}
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Test card 4242 4242 4242 4242</p>
          </>
        )}
      </div>
    </div>
  );
}
